import { useState, useCallback } from 'react';
import axios from 'axios';
import type { Product } from '@/types';
import { logger } from '@/lib/logger';

type DialogMode = 'rename' | 'delete' | null;

interface ProductApiResponse {
  success: boolean;
  data: {
    id: number | string;
    title: string;
    category: string;
    orientation: string;
    masterImageUrl: string | null;
    printFileUrls: Record<string, string> | null;
    googleDriveLink: string | null;
    finalPdfUrl: string | null;
    bunnyBackupSlug: string;
    mockupUrls: string[];
    createdAt: string;
    updatedAt: string;
  };
}

interface UseProductActionsOptions {
  onDeleted?: () => void;
  onRenamed?: (product: Product) => void;
  onRefresh?: () => Promise<void>;
}

export function useProductActions(options: UseProductActionsOptions = {}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openRenameDialog = useCallback((product: Product) => {
    setSelectedProduct(product);
    setDialogMode('rename');
    setNewTitle(product.title);
    setError(null);
    setDialogOpen(true);
  }, []);

  const openDeleteDialog = useCallback((product: Product) => {
    setSelectedProduct(product);
    setDialogMode('delete');
    setNewTitle('');
    setError(null);
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    if (submitting) return;
    setDialogOpen(false);
    setSelectedProduct(null);
    setDialogMode(null);
    setNewTitle('');
    setError(null);
  }, [submitting]);

  const handleDelete = useCallback(async (product: Product): Promise<boolean> => {
    try {
      await axios.delete(`/api/products/${product.id}`);
      return true;
    } catch (err) {
      logger.error('ProductActions', 'Failed to delete product', err);
      throw new Error('Failed to delete product');
    }
  }, []);

  const handleRename = useCallback(async (product: Product, title: string): Promise<Product> => {
    try {
      const response = await axios.patch<ProductApiResponse>(
        `/api/products/${product.id}`,
        { title }
      );

      if (!response.data?.success || !response.data.data) {
        throw new Error('Failed to update product');
      }

      const dbProd = response.data.data;
      return {
        ...product,
        id: String(dbProd.id),
        title: dbProd.title,
        category: dbProd.category as Product['category'],
        orientation: dbProd.orientation as Product['orientation'],
        masterImageUrl: dbProd.masterImageUrl,
        printFileUrls: dbProd.printFileUrls,
        googleDriveLink: dbProd.googleDriveLink,
        finalPdfUrl: dbProd.finalPdfUrl,
        bunnyBackupSlug: dbProd.bunnyBackupSlug,
        mockupUrls: dbProd.mockupUrls,
        createdAt: new Date(dbProd.createdAt),
        updatedAt: new Date(dbProd.updatedAt),
      };
    } catch (err) {
      logger.error('ProductActions', 'Failed to rename product', err);
      throw new Error('Failed to update product title');
    }
  }, []);

  const confirmDialog = useCallback(async () => {
    if (!selectedProduct || !dialogMode) return;

    try {
      setSubmitting(true);
      setError(null);

      if (dialogMode === 'delete') {
        await handleDelete(selectedProduct);
        closeDialog();
        options.onDeleted?.();
        options.onRefresh?.();
      } else if (dialogMode === 'rename') {
        const trimmed = newTitle.trim();
        if (!trimmed || trimmed === selectedProduct.title) {
          closeDialog();
          return;
        }

        const updatedProduct = await handleRename(selectedProduct, trimmed);
        closeDialog();
        options.onRenamed?.(updatedProduct);
        options.onRefresh?.();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  }, [selectedProduct, dialogMode, newTitle, handleDelete, handleRename, closeDialog, options]);

  return {
    dialogOpen,
    dialogMode,
    selectedProduct,
    newTitle,
    setNewTitle,
    submitting,
    error,
    openRenameDialog,
    openDeleteDialog,
    closeDialog,
    confirmDialog,
  };
}
